import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsTeamsService } from "@/services/analytics-teams-service";
import { TEAMS_TOP_LIMIT } from "../components/team/constants";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

export { TEAMS_TOP_LIMIT } from "../components/team/constants";

export const analyticsTeamsKeys = {
  all: ["analytics-teams"] as const,
  overview: (hotelId: string, from: string, to: string) =>
    [...analyticsTeamsKeys.all, "overview", hotelId, from, to] as const,
  topStaff: (hotelId: string, from: string, to: string) =>
    [...analyticsTeamsKeys.all, "top", "STAFF", hotelId, from, to] as const,
  topTeam: (hotelId: string, from: string, to: string) =>
    [...analyticsTeamsKeys.all, "top", "TEAM", hotelId, from, to] as const,
  staffSeries: (staffId: string, hotelId: string, from: string, to: string) =>
    [
      ...analyticsTeamsKeys.all,
      "staff-series",
      staffId,
      hotelId,
      from,
      to,
    ] as const,
};

export function useTeamsAnalytics(dateRange: DateRange, staffId?: string) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const { hotelId, from, to, isReady } = rangeParams;
  const previousRange = useMemo(
    () => getPreviousAnalyticsDateRange(dateRange),
    [dateRange],
  );
  const previousFrom = toAnalyticsDateParam(previousRange?.from);
  const previousTo = toAnalyticsDateParam(previousRange?.to);
  const hasPreviousRange = Boolean(previousFrom && previousTo);

  const overview = useQuery({
    queryKey: analyticsTeamsKeys.overview(hotelId, from, to),
    queryFn: () => analyticsTeamsService.getOverview({ hotelId, from, to }),
    enabled: isReady,
  });

  const previousOverview = useQuery({
    queryKey: analyticsTeamsKeys.overview(
      hotelId,
      previousFrom ?? "",
      previousTo ?? "",
    ),
    queryFn: () =>
      analyticsTeamsService.getOverview({
        hotelId,
        from: previousFrom!,
        to: previousTo!,
      }),
    enabled: isReady && hasPreviousRange,
  });

  const topStaff = useQuery({
    queryKey: analyticsTeamsKeys.topStaff(hotelId, from, to),
    queryFn: () =>
      analyticsTeamsService.getTop({
        hotelId,
        from,
        to,
        entity: "STAFF",
        limit: TEAMS_TOP_LIMIT,
      }),
    enabled: isReady,
  });

  const topTeam = useQuery({
    queryKey: analyticsTeamsKeys.topTeam(hotelId, from, to),
    queryFn: () =>
      analyticsTeamsService.getTop({
        hotelId,
        from,
        to,
        entity: "TEAM",
        limit: TEAMS_TOP_LIMIT,
      }),
    enabled: isReady,
  });

  const staffSeries = useQuery({
    queryKey: analyticsTeamsKeys.staffSeries(staffId ?? "", hotelId, from, to),
    queryFn: () =>
      analyticsTeamsService.getStaffSeries(staffId!, {
        hotelId,
        from,
        to,
      }),
    enabled: isReady && Boolean(staffId),
  });

  return {
    ...rangeParams,
    overview,
    previousOverview,
    topStaff,
    topTeam,
    staffSeries,
    isLoading:
      overview.isLoading ||
      previousOverview.isLoading ||
      topStaff.isLoading ||
      topTeam.isLoading ||
      staffSeries.isLoading,
    isFetching:
      overview.isFetching ||
      previousOverview.isFetching ||
      topStaff.isFetching ||
      topTeam.isFetching ||
      staffSeries.isFetching,
    isError:
      overview.isError ||
      previousOverview.isError ||
      topStaff.isError ||
      topTeam.isError ||
      staffSeries.isError,
    error:
      overview.error ??
      previousOverview.error ??
      topStaff.error ??
      topTeam.error ??
      staffSeries.error,
  };
}
